import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Checkbox,
  Select,
  Tooltip,
  useMediaQuery,
  useTheme,
  Radio,
  TextField,
} from "@mui/material";
import { userList } from "../../services/MemberService";
import AppContext from "../../config/AppContext";
import { useTranslation } from "react-i18next";
import { getAssignedUsersFromManagerProjects } from "../../services/ProjectService";
import CommonUtil from "../../util/CommonUtils";

export default function MemberFilter({
  memberFilter,
  setMemberFilter,
  setSelectedMemberName,
  // departmentFilter,
  projectFilter = "default",
  projects = []
}) {
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [members, setMembers] = useState([]);
  const [userID, setUserID] = useState(null);
  const { mode } = useContext(AppContext);
  const { t } = useTranslation();
  const [memberSearch, setMemberSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const role = CommonUtil.getRoleCode();
  const [allMembers, setAllMembers] = useState([]);

  const handleMemberFilterChange = (memberId) => {
    if (memberId === "All members") {
      const isAllSelected = selectedMembers.size === allMembers.length;
      const newSelectedMembers = isAllSelected ? new Set() : new Set(allMembers.map((member) => member.id));
      setSelectedMembers(newSelectedMembers);
      setMemberFilter(isAllSelected ? "" : "All members");
      setSelectedMemberName(t("RAL0409"));
      setOpen(false);
      return;
    }
    const newSelectedMembers = new Set([memberId]);
    setSelectedMembers(newSelectedMembers);
    setMemberFilter(memberId);
    const selectedMember = members.find((member) => member.id === memberId);
    setSelectedMemberName(selectedMember ? `${selectedMember.name}'s activities` : t("RAL0409"));
    setOpen(false);
  };

  const handleReset = () => {
    setMemberSearch("");
    setOpen(false);
  };

  useEffect(() => {
    if (
      projectFilter !== "default" &&
      projectFilter !== "" &&
      projectFilter !== "All projects") {

      const filterProjectIds = projectFilter?.split(",");

      const assignMemberInProject = projects.filter((project) =>
        filterProjectIds.includes(String(project.id)));

      if (assignMemberInProject?.length > 0) {
        const assignedMemberIds = new Set();
        assignMemberInProject.forEach(project => {
          project.assignedMembers?.forEach(memberId => assignedMemberIds.add(memberId));
        });
        const projectMembers = allMembers
          .filter(member => assignedMemberIds.has(member.id))
          .sort((a, b) => a.name.localeCompare(b.name));

        setMembers(projectMembers);
      } else {
        setMembers(allMembers);
      }
    } else {
      setMembers(allMembers);
    }

  }, [allMembers, projectFilter, projects]);

  useEffect(() => {
    if (projectFilter){
      setMemberSearch("");
      setMemberFilter("All members");
    }
  }, [projectFilter])

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        let updatedMembers = [];
        const res = await getAssignedUsersFromManagerProjects();
        updatedMembers = res?.data?.data?.map((user) => ({
          id: user?.user_id,
          name: user?.name,
          email: user?.email,
        }));

        updatedMembers?.sort((a, b) => a.name.localeCompare(b.name));
        setAllMembers(updatedMembers);

        const existingIds = Array.from(selectedMembers).filter(id =>
          updatedMembers.some(member => member.id === id)
        );
        if (existingIds && existingIds.length == 0) {
          setMemberFilter("All members");
          setSelectedMemberName(t("RAL0409"));
        }

        if (!memberFilter || memberFilter === "All members") {
          setMemberFilter("All members");
          setSelectedMemberName(t("RAL0409"));
          setSelectedMembers(new Set(updatedMembers.map((member) => member.id)));
        }
      } catch (error) {
        console.error("Error fetching members:", error);
      }
    }
    fetchMembers();
  }, [memberFilter, role, t]);

  return (
    <Box display="flex" alignItems="center">
      <Box sx={{ ml: 2 }}>
        <FormControl
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--border-color-1) !important",
                borderWidth: "1px !important",
              },
              "& .MuiSelect-select": {
                display: "block",
                width: "80%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          }}
        >
          <Select
            id="dashboard-member-select"
            fullWidth
            value={memberFilter}
            onClose={() => handleReset()}
            open={open}
            onOpen={() => {
              setMemberSearch("");
              setOpen(true)
            }}
            onChange={(e) => handleMemberFilterChange(e.target.value)}
            sx={{
              height: isXlScreen ? "50px" : "40px",
              width: "250px",
              color: "var(--text-primary)",
            }}
            renderValue={(selected) => {
              const selectedMember = members.find(
                (member) => member.id === selected
              );
              const memberName = selectedMember
                ? selectedMember.name.replace("null", "")
                : t("RAL0273");

              return  (
                <Tooltip title={memberName} placement="bottom">
                  <span>{memberName}</span>
                </Tooltip>
              )
            }}
            className="filterText"
            MenuProps={{
              PaperProps: {
                style: {
                  marginTop: "5px",
                  borderRadius: "5px",
                  boxShadow: "0px 3px 6px #00000026",
                },
                // sx: {
                //   maxHeight: 300,
                //   maxWidth: 220,
                // },
              },
              disableAutoFocusItem: true,
              autoFocus: false,
            }}
          >
            <MenuItem
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                fontFamily: "Gotham",
                fontStyle: "normal",
                fontVariant: "normal",
                fontWeight: "var(--fw-500)",
                backgroundColor:
                  mode === "dark" ? "#1F1F1F!important" : "#FFFFFF!important",
                color: mode === "dark" ? "#f8f8f8cc" : "#242424cc",
                "&:hover": {
                  backgroundColor: "#1F1F1F",
                },
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor:
                    mode === "dark" ? "#362C20!important" : "#FFF9F2!important",
                  color: "#FA9C2B",
                },
                position: "sticky",
                top: "0px",
                zIndex: "1",
              }}
            >
              <TextField
                id="select-search-memberfilter"
                placeholder={t("RAL0274")}
                variant="outlined"
                fullWidth
                size="small"
                value={memberSearch}
                autoFocus
                autoComplete="off"
                onChange={(e) => setMemberSearch(e.target.value)}
                InputLabelProps={{ shrink: false }}
                InputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                  },
                }}
                inputProps={{
                  maxLength: 50,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      border: "1px solid var(--border-color-1)",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1px solid var(--border-color-1)",
                    },
                    "& input::placeholder": {
                      color:
                        mode === "light"
                          ? "#24242480 !important"
                          : "#f8f8f880 !important",
                      opacity: 1,
                    },
                  },
                }}
              />
            </MenuItem>
            <Box
              sx={{
                maxHeight: 260,
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "var(--background-color-38) transparent",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "10px",
                },
              }}
            >

              <MenuItem
                id={`dashboard-dept-name-${"All members"}`}
                value="All members"
                onClick={() => handleMemberFilterChange("All members")}
                selected={selectedMembers?.size === allMembers?.length}
                sx={{
                  fontFamily: "Gotham",
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "var(--fw-500)",
                  backgroundColor: mode === "dark" ? "#1F1F1F" : "#FFFFFF",
                  color: mode === "dark" ? "#f8f8f8cc" : "#242424cc",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                    // color: "#FA9C2B",
                  },
                  "&.Mui-selected": {
                    backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                    color: "#FA9C2B",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                    color: "#FA9C2B",
                  },
                }}
              >
                <Radio
                  id={`dashboard-dept-name-${"All members"}`}
                  checked={selectedMembers.size === allMembers.length}
                  sx={{
                    "&.Mui-checked": {
                      color: "orange",
                    },
                  }}
                />
                {t("RAL0273")}
              </MenuItem>
              {members &&
                members
                  .filter((member) =>
                    member.name
                      ?.toLowerCase()
                      .includes(memberSearch.toLowerCase())
                  )
                  .map((member) => (
                    <MenuItem
                      id={`dashboard-dept-name-${member?.name}`}
                      key={member.id}
                      value={member.id}
                      onClick={() => handleMemberFilterChange(member.id)}
                      selected={selectedMembers.has(member.id)}
                      sx={{
                        fontFamily: "Gotham",
                        fontStyle: "normal",
                        fontVariant: "normal",
                        fontWeight: "var(--fw-500)",
                        backgroundColor:
                          mode === "dark" ? "#1F1F1F" : "#FFFFFF",
                        color: mode === "dark" ? "#f8f8f8cc" : "#242424cc",
                        "&:hover": {
                          backgroundColor:
                            mode === "dark" ? "#362C20" : "#FFF9F2",
                          // color: "#FA9C2B",
                        },
                        "&.Mui-selected": {
                          backgroundColor:
                            mode === "dark" ? "#362C20" : "#FFF9F2",
                          color: "#FA9C2B",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor:
                            mode === "dark" ? "#362C20" : "#FFF9F2",
                          color: "#FA9C2B",
                        },
                      }}
                    >
                      <Radio
                        id={`dashboard-dept-name-${member?.name}`}
                        checked={selectedMembers.has(member?.id)}
                        sx={{
                          "&.Mui-checked": {
                            color: "orange",
                          },
                        }}
                      />
                      {member?.name?.length > 14 ? (
                        <Tooltip
                          id={`dashboard-dept-name-${member?.name}`}
                          title={member.name}
                          placement="top"
                        // arrow//
                        >
                          <span id={`dashboard-dept-name-${member?.name}`}>{`${member?.name
                            .replace("null", "")
                            .substring(0, 14)}...`}</span>
                        </Tooltip>
                      ) : (
                        member?.name.replace("null", "")
                      )}
                    </MenuItem>
                  ))}
            </Box>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
